import React, { useEffect, useState } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material'

const GET_SETTINGS = gql`
  query DispatchSettings {
    dispatchSettings {
      _id
      platformDispatchMode
      staffFallbackTimeoutSec
      platformFallbackTimeoutSec
      manualDispatchTimeoutSec
      riderOutOfZoneToleranceMiles
      firstDeliveryFallbackEnabled
      allowSelfDeliveryFallback
      maxOrdersPerRider
      rushmyfoodDeliveryProvider
    }
  }
`

const SAVE_SETTINGS = gql`
  mutation SaveDispatchSettings($input: DispatchSettingsInput!) {
    saveDispatchSettings(input: $input) {
      _id
      platformDispatchMode
      staffFallbackTimeoutSec
      platformFallbackTimeoutSec
      manualDispatchTimeoutSec
      riderOutOfZoneToleranceMiles
      firstDeliveryFallbackEnabled
      allowSelfDeliveryFallback
      maxOrdersPerRider
      rushmyfoodDeliveryProvider
    }
  }
`

export default function DispatchSettings() {
  const { data, loading, error } = useQuery(GET_SETTINGS, { fetchPolicy: 'cache-and-network' })
  const [save, { loading: saving }] = useMutation(SAVE_SETTINGS, {
    onCompleted: () => setMessage({ type: 'success', text: 'Settings saved.' }),
    onError: e => setMessage({ type: 'error', text: e.message }),
  })

  const [form, setForm] = useState({
    platformDispatchMode: 'BROADCAST',
    staffFallbackTimeoutSec: 60,
    platformFallbackTimeoutSec: 120,
    manualDispatchTimeoutSec: 300,
    riderOutOfZoneToleranceMiles: 0,
    firstDeliveryFallbackEnabled: true,
    allowSelfDeliveryFallback: true,
    maxOrdersPerRider: 2,
    rushmyfoodDeliveryProvider: 'PLATFORM_FIRST_FD_FALLBACK',
  })
  const [message, setMessage] = useState(null)

  useEffect(() => {
    if (data?.dispatchSettings) {
      setForm({
        platformDispatchMode: data.dispatchSettings.platformDispatchMode,
        staffFallbackTimeoutSec: data.dispatchSettings.staffFallbackTimeoutSec,
        platformFallbackTimeoutSec: data.dispatchSettings.platformFallbackTimeoutSec,
        manualDispatchTimeoutSec: data.dispatchSettings.manualDispatchTimeoutSec ?? 300,
        riderOutOfZoneToleranceMiles: data.dispatchSettings.riderOutOfZoneToleranceMiles ?? 0,
        firstDeliveryFallbackEnabled: !!data.dispatchSettings.firstDeliveryFallbackEnabled,
        allowSelfDeliveryFallback: !!data.dispatchSettings.allowSelfDeliveryFallback,
        maxOrdersPerRider: data.dispatchSettings.maxOrdersPerRider ?? 2,
        rushmyfoodDeliveryProvider: data.dispatchSettings.rushmyfoodDeliveryProvider || 'PLATFORM_FIRST_FD_FALLBACK',
      })
    }
  }, [data])

  const onSubmit = (e) => {
    e.preventDefault()
    setMessage(null)
    save({ variables: { input: form } })
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Global Dispatch Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Controls how new orders are routed to drivers across the platform.
        </Typography>

        {loading && !data && <CircularProgress />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
        {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

        {!loading && !error && (
          <Box component="form" onSubmit={onSubmit}>
            <Stack spacing={3}>
              <TextField
                select
                fullWidth
                label="Platform rider push mode"
                value={form.platformDispatchMode}
                onChange={e => setForm({ ...form, platformDispatchMode: e.target.value })}>
                <MenuItem value="BROADCAST">Broadcast to all eligible riders</MenuItem>
                <MenuItem value="CLOSEST_FIRST">Push to nearest rider only</MenuItem>
              </TextField>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Staff fallback timeout (sec)"
                  inputProps={{ min: 15, max: 600 }}
                  value={form.staffFallbackTimeoutSec}
                  onChange={e => setForm({ ...form, staffFallbackTimeoutSec: parseInt(e.target.value, 10) || 0 })}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Platform fallback timeout (sec)"
                  inputProps={{ min: 15, max: 600 }}
                  value={form.platformFallbackTimeoutSec}
                  onChange={e => setForm({ ...form, platformFallbackTimeoutSec: parseInt(e.target.value, 10) || 0 })}
                />
              </Stack>

              <TextField
                fullWidth
                type="number"
                label="Rider out-of-zone tolerance (miles)"
                helperText="Riders must be inside their delivery zone (or within this many miles of its edge) to receive orders. 0 = strict, must be inside the polygon. Raise it for flexibility."
                inputProps={{ min: 0, max: 50, step: 0.5 }}
                value={form.riderOutOfZoneToleranceMiles}
                onChange={e => setForm({ ...form, riderOutOfZoneToleranceMiles: parseFloat(e.target.value) || 0 })}
              />

              <TextField
                fullWidth
                type="number"
                label="Manual dispatch timeout (sec)"
                helperText="If a MANUAL self-delivery restaurant hasn't assigned a driver within this window, the order auto-falls through to platform riders (requires self-delivery fallback ON)."
                inputProps={{ min: 60, max: 3600 }}
                value={form.manualDispatchTimeoutSec}
                onChange={e => setForm({ ...form, manualDispatchTimeoutSec: parseInt(e.target.value, 10) || 0 })}
              />

              <TextField
                fullWidth
                type="number"
                label="Max in-progress orders per rider"
                helperText="A rider holding this many active orders won't be offered more. Recommended: 2."
                inputProps={{ min: 1, max: 10 }}
                value={form.maxOrdersPerRider}
                onChange={e => setForm({ ...form, maxOrdersPerRider: parseInt(e.target.value, 10) || 1 })}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.allowSelfDeliveryFallback}
                    onChange={e => setForm({ ...form, allowSelfDeliveryFallback: e.target.checked })}
                  />
                }
                label="Allow restaurant self-delivery orders to fall back to platform riders"
              />

              <TextField
                select
                fullWidth
                label="RushMyFood delivery provider"
                helperText="Who delivers non-self-delivery orders. Self-delivery restaurants use their own staff and are not affected."
                value={form.rushmyfoodDeliveryProvider}
                onChange={e => setForm({ ...form, rushmyfoodDeliveryProvider: e.target.value })}>
                <MenuItem value="PLATFORM_ONLY">Platform riders only (never call First Delivery)</MenuItem>
                <MenuItem value="FIRST_DELIVERY_ONLY">First Delivery only (skip platform riders)</MenuItem>
                <MenuItem value="PLATFORM_FIRST_FD_FALLBACK">Platform first, fall back to First Delivery (recommended)</MenuItem>
              </TextField>

              <Box>
                <Button type="submit" variant="contained" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Settings'}
                </Button>
              </Box>
            </Stack>
          </Box>
        )}
      </Paper>
    </Container>
  )
}
